// GID-105 read-on-send.
//
// The VSTO sidecar used to re-read the whole project over COM on the MS Project
// UI thread every ~8s heartbeat (the "MSP slowness" cause). Instead, the taskpane
// asks for a fresh read only when it's about to send a message, and waits briefly
// for it to land before reading context.
//
// This helper is dependency-injected (no DOM/module globals) so it can be unit
// tested in isolation. It is best-effort: it resolves as soon as the backend
// reports our request fulfilled, or after a short timeout — in which case the
// caller proceeds with whatever snapshot already exists (old connectors still
// push on their own heartbeat, so it's never worse than the pre-GID-105 path).

/**
 * We poll the tiny snapshot-request status rather than the full snapshot endpoint
 * (getActiveProject is ~2MB on a 1000-task plan, and we only need a yes/no here):
 * once the sidecar posts the fresh snapshot the backend clears the request, so
 * "our id is no longer pending" is the signal that the read landed.
 *
 * The 4s budget is about the pane's own patience, not a promise the connector will
 * answer that fast: the request-driven read only lands within it if the sidecar's
 * 10s reconciliation floor happens to fire in time, so it succeeds some of the time,
 * not most. A timeout here isn't an error — it just means the message goes out on
 * whatever snapshot the backend already holds, and COM change-event pushes (a
 * debounced full read on edit, usually within ~5s) generally keep that current.
 *
 * @param {Object} deps
 * @param {(windowToken: string|null) => Promise<{request_id?: string}|null>} deps.requestSnapshot
 * @param {() => Promise<{requested?: boolean, request_id?: string}|null>} deps.getSnapshotRequestStatus
 * @param {string|null} [deps.windowToken]
 * @param {number} [deps.timeoutMs]
 * @param {number} [deps.pollMs]
 * @param {(ms: number) => Promise<void>} [deps.sleep]
 * @param {() => number} [deps.now]
 * @returns {Promise<boolean>} true if a fresh snapshot landed; false if we fell back
 */
export async function awaitFreshSnapshot({
  requestSnapshot,
  getSnapshotRequestStatus,
  windowToken = null,
  timeoutMs = 4000,
  pollMs = 250,
  sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  now = () => Date.now(),
} = {}) {
  try {
    const requested = await requestSnapshot(windowToken);
    const requestId = requested && requested.request_id;
    if (!requestId) return false; // no backend support / old path — use existing snapshot

    const deadline = now() + timeoutMs;
    while (now() < deadline) {
      // Scoped to OUR window: the backend must not answer with another pane's request.
      const status = await getSnapshotRequestStatus(windowToken);
      // Only "nothing pending for this window" means our read landed. A pending
      // entry with a different id is NOT fulfilment — it is a newer request for
      // this window, and treating it as success sent the message on stale context.
      // null = transport blip: keep waiting rather than falsely claiming success.
      if (status && status.requested === false) {
        return true; // our request was cleared => the sidecar's fresh read landed
      }
      await sleep(pollMs);
    }
    return false; // timed out — caller uses the existing snapshot
  } catch (error) {
    return false; // best-effort; never block a send
  }
}

/**
 * How long a send waits for the connector's fresh read. Longer than a held poll
 * on a dead connection takes to give up and reconnect (15s), so a connector that
 * is online but momentarily unreachable still gets the read in.
 */
export const FRESH_PLAN_WAIT_MS = 20000;

/** Wait this long before telling the user a read is under way, so quick reads show nothing. */
const WAITING_NOTICE_MS = 1000;

/**
 * Ask the connector for a fresh read of the plan and wait for it, reporting why
 * when it cannot be had -- so a message is sent on a plan read for it, or the
 * user decides otherwise. Supersedes the 4s best-effort awaitFreshSnapshot
 * above, which quietly sent on whatever copy the backend held.
 *
 * Statuses:
 *  - "fresh":       the connector's read for this request landed
 *  - "offline":     the backend has not heard from the connector recently, so
 *                   waiting cannot help; reported at once
 *  - "timeout":     the connector is online but did not answer in timeoutMs
 *  - "error":       the read request itself could not be made
 *  - "unsupported": the backend does not run read requests (old behaviour applies)
 *
 * @param {Object} deps  same as awaitFreshSnapshot, plus:
 * @param {() => void} [deps.onWaiting]  called once if the wait passes ~1s
 * @returns {Promise<{status: string, connectorSeenSecondsAgo: number|null}>}
 */
export async function awaitFreshPlan({
  requestSnapshot,
  getSnapshotRequestStatus,
  windowToken = null,
  timeoutMs = FRESH_PLAN_WAIT_MS,
  pollMs = 250,
  onWaiting = null,
  sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  now = () => Date.now(),
} = {}) {
  let requested;
  try {
    requested = await requestSnapshot(windowToken);
  } catch {
    requested = null;
  }
  if (!requested) return { status: "error", connectorSeenSecondsAgo: null, waitedMs: 0 };
  const requestId = requested.request_id;
  if (!requestId) return { status: "unsupported", connectorSeenSecondsAgo: null, waitedMs: 0 };

  const seen = Number.isFinite(requested.connector_seen_seconds_ago)
    ? requested.connector_seen_seconds_ago
    : null;
  // Absent means a backend that does not track check-ins: assume online and wait.
  if (requested.connector_online === false) {
    return { status: "offline", connectorSeenSecondsAgo: seen, waitedMs: 0 };
  }

  // The server decides how long it keeps honouring this request; waiting less
  // than that threw away reads that did land. FRESH_PLAN_WAIT_MS stays only as
  // the fallback for a backend too old to send request_ttl_ms.
  const serverTtl = Number(requested.request_ttl_ms);
  const effectiveTimeoutMs =
    Number.isFinite(serverTtl) && serverTtl > 0 ? serverTtl : timeoutMs;
  const started = now();
  const deadline = started + effectiveTimeoutMs;
  let toldWaiting = false;
  while (now() < deadline) {
    let status = null;
    try {
      status = await getSnapshotRequestStatus(windowToken);
    } catch {
      status = null; // transport blip: keep waiting
    }
    // Only "nothing pending for this window" means our read landed; see
    // awaitFreshSnapshot for why a different pending id is not fulfilment.
    if (status && status.requested === false) {
      return { status: "fresh", connectorSeenSecondsAgo: seen, waitedMs: now() - started };
    }
    if (!toldWaiting && onWaiting && now() - started >= WAITING_NOTICE_MS) {
      toldWaiting = true;
      try { onWaiting(); } catch { /* a UI callback must not break the wait */ }
    }
    await sleep(pollMs);
  }
  return { status: "timeout", connectorSeenSecondsAgo: seen, waitedMs: effectiveTimeoutMs };
}
